import { X, Home, Package, Settings, LogOut, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../hooks/use-toast";
import { FormattedMessage, useIntl } from "react-intl";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { LogoutAPI } from "../features/auth/authAPI";

function NavigationDrawer({ isOpen, onClose, onNavigate }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { profile } = useSelector((state) => state.settings);
  const handleNavigate = (page) => {
    onNavigate(page);
    onClose();
  };

  async function handleLogout() {
    try {
      toast({
        title: intl.formatMessage({
          id: "auth.logginOut",
          defaultMessage: "Logging Out...",
        }),
        description: intl.formatMessage({
          id: "auth.logginOutWait",
          defaultMessage: "Please wait while we log you out.",
        }),
      });
      const keysToKeep = ["locale", "biometricEnabled", "darkMode"];
      Object.keys(localStorage)
        .filter((key) => !keysToKeep.includes(key))
        .forEach((key) => localStorage.removeItem(key));

      await dispatch(LogoutAPI());
      onClose();

      toast({
        title: intl.formatMessage({
          id: "auth.loggedOut",
          defaultMessage: "Logging out...",
        }),
        description: intl.formatMessage({
          id: "auth.loggedOutdescription",
          defaultMessage: "You have been securely logged out.",
        }),
      });
    } catch (error) {
      console.error("Logout error:", error);

      localStorage.removeItem("token_bearer");
      localStorage.removeItem("persist:root");

      dispatch(LogoutAPI());
      onClose();

      toast({
        title: intl.formatMessage({
          id: "auth.logoutErrorTitle",
          defaultMessage: "Logout Error",
        }),
        description: intl.formatMessage({
          id: "auth.logoutErrorDesc",
          defaultMessage: "Failed to log out. Please try again.",
        }),
        variant: "error",
      });
    }
  }

  const employee = {
    name: localStorage.getItem("userName"),
    empId: localStorage.getItem("EmployeeNumber"),
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="left" className="w-80 !p-0">
        <SheetHeader className="p-6 border-b dark:border-gray-700 !text-left">
          <SheetTitle className="mb-4">TechDispatch</SheetTitle>
          <div className="flex items-center mt-6 space-x-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback>
                {employee.name ? employee.name.charAt(0) : "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">{employee.name}</p>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="navigation.empId" defaultMessage="ID:" />{" "}
                {employee.empId}
              </p>
            </div>
          </div>
        </SheetHeader>

        <nav className="!p-4 space-y-2">
          <Button
            variant="ghost"
            className="w-full !justify-start !text-left"
            onClick={() => handleNavigate("dashboard")}
          >
            <Home className="mr-3 h-5 w-5 text-indigo-500" />
            <FormattedMessage
              id="navigation.dashboard"
              defaultMessage="Dashboard"
            />
          </Button>

          <Button
            variant="ghost"
            className="w-full !justify-start text-left"
            onClick={() => handleNavigate("inventory")}
          >
            <Package className="mr-3 h-5 w-5 text-green-500" />
            <FormattedMessage
              id="navigation.inventory"
              defaultMessage="Inventory"
            />
          </Button>

          <Button
            variant="ghost"
            className="w-full !justify-start text-left"
            onClick={() => handleNavigate("availability")}
          >
            <Calendar className="mr-3 h-5 w-5 text-blue-500" />
            <FormattedMessage
              id="navigation.availability"
              defaultMessage="Availability"
            />
          </Button>

          <Button
            variant="ghost"
            className="w-full !justify-start text-left"
            onClick={() => handleNavigate("settings")}
          >
            <Settings className="mr-3 h-5 w-5 text-gray-500" />
            <FormattedMessage
              id="navigation.settings"
              defaultMessage="Settings"
            />
          </Button>

          <hr className="my-4 border-gray-200 dark:border-gray-700" />

          <Button
            variant="ghost"
            className="w-full !justify-start text-left text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            onClick={handleLogout}
          >
            <LogOut className="mr-3 h-5 w-5" />
            <FormattedMessage id="navigation.logout" defaultMessage="Logout" />
          </Button>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export default NavigationDrawer;
